import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Handshake, ExternalLink, Globe } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENESIS_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/d5b6a475-12ed-4057-ae9d-2a4f013301cd/images/8339d6b61e3338b3b1ede691e9b17f01f31f8cda85900cdf8984dbce4e90621c.png';
const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

export default function PartnersPage() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners`);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  // Featured partners that always show at top
  const featuredPartners = [
    {
      id: 'featured-1',
      name: 'Smallest Souls Animal Rescue',
      description: '501(c)(3) nonprofit organization dedicated to rescuing, rehabilitating, and rehoming animals in need.',
      website: 'https://www.smallestsoulsanimalrescue.com/',
      logo: '',
      featured: true
    },
    {
      id: 'featured-2',
      name: 'Foamblasterz',
      description: 'A fun and exciting entertainment partner bringing joy to community events.',
      website: 'https://foamblasterz.com/',
      logo: '',
      featured: true
    },
    {
      id: 'featured-3',
      name: 'Odin Delgado - Heritage International Realty',
      description: 'Trusted realtor helping families find their dream homes. Office: (407) 954-2498 | Cell: (407) 613-9157',
      website: '',
      logo: '',
      featured: true
    },
    {
      id: 'featured-4',
      name: 'Joanne Walker - Smart International Title',
      description: 'Title Closer/Notary & Customer Service Specialist with half a century of unparalleled customer service. Phone: (407) 854-2115',
      website: 'https://smartinternationaltitle.com/joanne/',
      logo: '',
      featured: true
    }
  ];

  // Default partners if API returns empty
  const defaultPartners = [
    {
      id: '1',
      name: 'Second Harvest Food Bank',
      description: 'Central Florida\'s leading food bank, helping us source quality food for families in need.',
      website: 'https://feedhopenow.org',
      logo: ''
    },
    {
      id: '2',
      name: 'Kissimmee Community Church Network',
      description: 'A network of local churches working together to serve the Kissimmee community.',
      website: '',
      logo: ''
    },
    {
      id: '3',
      name: 'Osceola County Schools',
      description: 'Partnering to support students and families through various outreach programs.',
      website: 'https://osceolaschools.net',
      logo: ''
    }
  ];

  // Always show featured partners first, then API partners or defaults
  const apiOrDefaultPartners = partners.length > 0 ? partners : defaultPartners;
  const displayPartners = [...featuredPartners, ...apiOrDefaultPartners];

  return (
    <div 
      data-testid="partners-page"
      style={{
        minHeight: '100vh',
        paddingTop: '80px'
      }}
    >
      {/* Genesis Image as Header */}
      <div style={{ width: '100%', overflow: 'hidden', maxHeight: '400px' }}>
        <img 
          src={GENESIS_IMAGE} 
          alt="Genesis 2:15 - The Lord God took the man and put him in the Garden of Eden to work it and take care of it."
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            marginTop: '-100px'
          }}
        />
      </div>

      {/* Hero Section */}
      <section style={{ padding: '64px 0', position: 'relative', backgroundColor: '#F0FFF0' }}>
        <div style={{ maxWidth: '1152px', margin: '0 auto', padding: '0 24px' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center', marginBottom: '48px' }}
          >
            <div style={{ 
              width: '64px', 
              height: '64px', 
              backgroundColor: '#228B22', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              margin: '0 auto 24px'
            }}>
              <Handshake size={32} color="white" />
            </div>
            <span style={{ 
              fontSize: '12px', 
              textTransform: 'uppercase', 
              letterSpacing: '0.2em', 
              color: '#000000', 
              marginBottom: '16px', 
              display: 'block',
              fontWeight: 'bold',
              textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
            }}>
              Working Together
            </span>
            <h1 style={{ 
              fontFamily: 'Playfair Display, serif', 
              fontSize: '48px', 
              color: '#1A1A1A', 
              marginBottom: '16px',
              textShadow: '2px 2px 4px rgba(255,255,255,0.8)'
            }}>
              Our Partners
            </h1>
            <p style={{ 
              color: '#333333', 
              maxWidth: '672px', 
              margin: '0 auto',
              textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
            }}>
              We're grateful for the organizations that partner with us to serve our community. Together, we can do more.
            </p>
          </motion.div>

          {/* Partners Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '32px' 
          }}>
            {displayPartners.map((partner, i) => (
              <motion.div
                key={partner.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '16px',
                  padding: '24px',
                  border: '1px solid rgba(144, 238, 144, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                data-testid={`partner-card-${i}`}
              >
                {/* Logo placeholder */}
                <div style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#F0FFF0',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}>
                  {partner.logo ? (
                    <img src={partner.logo} alt={partner.name} style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
                  ) : (
                    <Handshake size={24} color="#228B22" />
                  )}
                </div>
                
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1A1A1A', marginBottom: '8px' }}>
                  {partner.name}
                </h3>
                <p style={{ color: '#666666', fontSize: '14px', marginBottom: '16px' }}>
                  {partner.description}
                </p>
                
                {partner.website && (
                  <a
                    href={partner.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      color: '#228B22',
                      textDecoration: 'none'
                    }}
                  >
                    <Globe size={14} /> Visit Website <ExternalLink size={12} />
                  </a>
                )}
              </motion.div>
            ))}
          </div>

          {displayPartners.length === 0 && !loading && (
            <div style={{
              textAlign: 'center',
              padding: '48px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '16px',
              border: '1px solid rgba(144, 238, 144, 0.3)'
            }}>
              <Handshake size={48} style={{ margin: '0 auto 16px', color: '#cccccc' }} />
              <p style={{ color: '#999999' }}>No partners listed yet.</p>
            </div>
          )}

          {/* Become a Partner CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{
              marginTop: '64px',
              backgroundColor: '#228B22',
              borderRadius: '24px',
              padding: '48px',
              textAlign: 'center'
            }}
          >
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '32px', color: 'white', marginBottom: '16px' }}>
              Become a Partner
            </h2>
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', maxWidth: '576px', margin: '0 auto 24px' }}>
              Is your organization interested in partnering with Living Fruit Ministry? We'd love to explore how we can work together to serve our community.
            </p>
            <a
              href="/contact"
              style={{
                display: 'inline-block',
                backgroundColor: '#90EE90',
                color: '#1A1A1A',
                padding: '16px 32px',
                borderRadius: '50px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontSize: '12px',
                textDecoration: 'none'
              }}
              data-testid="partner-contact-btn"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
